import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useBudgetQueries, useFringeQuery } from 'utils/api/queries';
import { formatCurrency } from 'utils/helper';

const collectSheets = root => {
  const sheets = [];
  const visit = node => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    const candidates = [
      node.swimlane_detail_sheets,
      node.swimlane_item_detail_sheets,
      node.line_item_detail_sheets,
      node.detail_sheets,
    ];
    candidates.forEach(c => {
      if (Array.isArray(c)) {
        c.forEach(sheet => {
          if (sheet && typeof sheet === 'object') sheets.push(sheet);
        });
      }
    });
    const childContainers = [
      node.budget_swimlane,
      node.swimlanes,
      node.swimlane_items,
      node.children,
      node.items,
      node.data,
    ];
    childContainers.forEach(visit);
  };
  visit(root);
  return sheets;
};

const definitionsArray = response => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  return [];
};

const fringeLabel = obj =>
  obj?.display_name || obj?.name || obj?.full_name || 'Fringe';

const POPOVER_RESIZE_SETTLE_MS = 200;

const FringeTotalPopover = ({ currency, children }) => {
  const { budgetData, selectedBudget } = useHomeScreenContext();
  const tabId = selectedBudget?.id;
  const [showPopover, setShowPopover] = useState(false);
  const isViewportResizingRef = useRef(false);
  const resizeTimerRef = useRef(null);

  useEffect(() => {
    const handleViewportResize = () => {
      isViewportResizingRef.current = true;
      setShowPopover(false);
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        resizeTimerRef.current = null;
        isViewportResizingRef.current = false;
      }, POPOVER_RESIZE_SETTLE_MS);
    };

    window.addEventListener('resize', handleViewportResize);
    return () => {
      window.removeEventListener('resize', handleViewportResize);
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
    };
  }, []);

  const handlePopoverToggle = nextShow => {
    if (isViewportResizingRef.current) {
      setShowPopover(false);
      return;
    }
    setShowPopover(nextShow);
  };

  const { data: fringeDefsResponse } = useFringeQuery.useFringeDetailsQuery({
    enabled: Boolean(tabId && !selectedBudget?.is_optimistic),
    filters: { instance_type: 'budget', active_id: tabId },
  });

  const { data: swimlaneQueryData } = useBudgetQueries.useSwimlaneQuery({
    enabled: Boolean(selectedBudget?.budget),
    payload: { id: selectedBudget?.budget, type: 'swimlane' },
  });
  const swimlaneItems = swimlaneQueryData?.data;

  const { rows, sumOfTotals } = useMemo(() => {
    const definitions = definitionsArray(fringeDefsResponse);

    const rowsById = new Map();
    const upsert = (id, partial) => {
      const existing = rowsById.get(id) || { id, label: '', total: 0 };
      rowsById.set(id, {
        id,
        label: partial.label || existing.label,
        total: existing.total + (partial.totalDelta || 0),
      });
    };

    definitions.forEach(def => {
      if (def?.id == null) return;
      upsert(`fringe-${def.id}`, { label: fringeLabel(def), totalDelta: 0 });
    });

    const defsByName = new Map();
    definitions.forEach(def => {
      const label = fringeLabel(def);
      if (label && def?.id != null) {
        defsByName.set(label, `fringe-${def.id}`);
      }
    });

    const sheets = [
      ...collectSheets(budgetData),
      ...collectSheets(swimlaneItems),
    ];

    sheets.forEach(sheet => {
      const fringes = Array.isArray(sheet?.fringe) ? sheet.fringe : [];
      fringes.forEach(entry => {
        if (entry?.type === 'group') {
          const breakdown = entry?.fringe_breakdown || {};
          Object.entries(breakdown).forEach(([memberLabel, value]) => {
            const memberContribution = Number(value) || 0;
            if (!memberContribution) return;
            const matchedId =
              defsByName.get(memberLabel) || `name:${memberLabel}`;
            upsert(matchedId, {
              label: memberLabel,
              totalDelta: memberContribution,
            });
          });
        } else {
          const contribution = Number(entry?.fringe_total) || 0;
          if (!contribution) return;
          const id = entry?.id != null ? `fringe-${entry.id}` : null;
          if (id) {
            upsert(id, {
              label: fringeLabel(entry),
              totalDelta: contribution,
            });
          } else {
            const label = fringeLabel(entry);
            upsert(`name:${label}`, { label, totalDelta: contribution });
          }
        }
      });
    });

    // Dedupe by label so that the same fringe doesn't appear twice when the
    // definitions list and the applied data carry it under different ids
    // (happens when a fringe is added inline in the detail view and never
    // synced back into the budget fringe list).
    const byLabel = new Map();
    rowsById.forEach(row => {
      if (!row.label) return;
      const existing = byLabel.get(row.label);
      if (existing) {
        existing.total += row.total;
      } else {
        byLabel.set(row.label, { ...row });
      }
    });

    const allRows = Array.from(byLabel.values());
    const sum = allRows.reduce((acc, row) => acc + row.total, 0);

    allRows.sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.label.localeCompare(b.label);
    });

    return { rows: allRows, sumOfTotals: sum };
  }, [fringeDefsResponse, budgetData, swimlaneItems]);

  const popover = (
    <Popover id="fringe-breakdown-popover" className="fringe-breakdown-popover">
      <Popover.Header as="h6">Fringe Breakdown</Popover.Header>
      <Popover.Body>
        {rows.length === 0 ? (
          <div className="fringe-breakdown-empty">
            No fringes in this budget.
          </div>
        ) : (
          rows.map(row => {
            const pct = sumOfTotals > 0 ? (row.total / sumOfTotals) * 100 : 0;
            return (
              <div className="fringe-breakdown-row" key={row.id}>
                <span className="fringe-breakdown-row__label">{row.label}</span>
                <span className="fringe-breakdown-row__amount">
                  {formatCurrency(row.total, currency)}
                </span>
                <span className="fringe-breakdown-row__pct">
                  {pct.toFixed(1)}%
                </span>
              </div>
            );
          })
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      delay={{ show: 180, hide: 60 }}
      show={showPopover}
      onToggle={handlePopoverToggle}
      placement="top"
      flip
      popperConfig={{
        modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }],
      }}
      overlay={popover}
    >
      {children}
    </OverlayTrigger>
  );
};

FringeTotalPopover.propTypes = {
  currency: PropTypes.string,
  children: PropTypes.element.isRequired,
};

FringeTotalPopover.defaultProps = {
  currency: 'USD',
};

export default FringeTotalPopover;
